import {
    AppNavigationContextProvider,
    AppOutlet,
    AppRouteObject,
    PageContextProvider,
    PageOverlayProvider,
    UnsavedChangesContextProvider,
} from '@faro/app-context';
import { AuthenticatedRoute } from '@faro/auth';
import { SpaceListPageSkeleton } from '@faro/study-space-ui';
import React, { Suspense } from 'react';
import { AppInsightsConnector } from './component/AppInsightsConnector/AppInsightsConnector';
import { ErrorBoundary } from './component/ErrorBoundary/ErrorBoundary';
import ErrorMessage from './component/ErrorMessage/ErrorMessage';
import FeatureFlagsInitializer from './component/FeatureFlagsInitializer/FeatureFlagsInitializer';
import { NavLayout } from './component/NavLayout/NavLayout';
import { AppAnalyticsProvider } from './package/analytics/AppAnalyticsProvider';
import { AuthenticationSpinner, AuthProvider, SignedOffMessage } from './package/auth';
import { AppRootAuthorizedRoute } from './package/auth/AppRootAuthorizedRoute';
import SessionExpiredMessage from './package/auth/SessionExpiredMessage/SessionExpiredMessage';
import ScrollToTop from './package/react-router-dom/ScrollToTop';
import Home from './page/Home';
import { NotFound } from './page/NotFound/NotFound';

const SpacesModule = React.lazy(() => import('@faro/study-space-ui'));
const ContentLibraryModule = React.lazy(() => import('@faro/content-library-ui'));
const ProtocolImportModule = React.lazy(() => import('@faro/protocol-import-ui'));
const ProtocolImportProtocolModule = React.lazy(() => import('@faro/protocol-import-ui/indexLab'));
const UserManagementModule = React.lazy(() => import('@faro/user-management-ui'));
const StudyDesignerModule = React.lazy(() => import('@faro/study-designer-ui'));
const ProtocolOptimizationModule = React.lazy(() => import('@faro/protocol-optimization-ui'));

const rootOutletContext = { depth: 0 };

const routes = [
    {
        path: '/',
        element: (
            <ErrorBoundary>
                <AuthProvider>
                    <AppAnalyticsProvider>
                        <PageContextProvider>
                            <PageOverlayProvider>
                                <AppNavigationContextProvider defaultApplicationTitle="Faro">
                                    <UnsavedChangesContextProvider>
                                        <ScrollToTop />
                                        <AppOutlet context={rootOutletContext} />
                                    </UnsavedChangesContextProvider>
                                </AppNavigationContextProvider>
                            </PageOverlayProvider>
                        </PageContextProvider>
                        <AppInsightsConnector />
                    </AppAnalyticsProvider>
                </AuthProvider>
            </ErrorBoundary>
        ),
        children: [
            {
                path: '/*',
                element: (
                    <FeatureFlagsInitializer>
                        <AuthenticatedRoute
                            loading={<AuthenticationSpinner />}
                            errorHandler={(error, message) => <ErrorMessage error={error} message={message} />}
                        >
                            <NavLayout>
                                <AppOutlet />
                            </NavLayout>
                        </AuthenticatedRoute>
                    </FeatureFlagsInitializer>
                ),
                children: [
                    {
                        index: true,
                        element: <Home />,
                    },
                    {
                        // Needed to support auth flow
                        path: 'auth/openid/return',
                        element: <AuthenticationSpinner />,
                    },
                    {
                        path: 'spaces/*',
                        breadcrumb: {
                            name: 'Spaces',
                        },
                        element: (
                            <Suspense fallback={<SpaceListPageSkeleton />} key="spaces">
                                <AppRootAuthorizedRoute appName="study-spaces">
                                    <SpacesModule />
                                </AppRootAuthorizedRoute>
                            </Suspense>
                        ),
                    } as AppRouteObject,
                    {
                        path: 'studies/*',
                        breadcrumb: {
                            name: 'Study Designer',
                        },
                        element: (
                            <Suspense fallback={null} key="studies">
                                <AppRootAuthorizedRoute appName="study-designer">
                                    <StudyDesignerModule />
                                </AppRootAuthorizedRoute>
                            </Suspense>
                        ),
                    } as AppRouteObject,
                    {
                        path: 'libraries/*',
                        breadcrumb: {
                            name: 'Library',
                        },
                        element: (
                            <Suspense fallback={null} key="libraries">
                                <AppRootAuthorizedRoute appName="content-library">
                                    <ContentLibraryModule />
                                </AppRootAuthorizedRoute>
                            </Suspense>
                        ),
                    } as AppRouteObject,
                    {
                        path: 'user-management/*',
                        breadcrumb: {
                            name: 'User Management',
                        },
                        element: (
                            <Suspense fallback={null} key="user-management">
                                <AppRootAuthorizedRoute appName="user-management">
                                    <UserManagementModule />
                                </AppRootAuthorizedRoute>
                            </Suspense>
                        ),
                    } as AppRouteObject,
                    {
                        path: 'protocol-import/*',
                        breadcrumb: {
                            name: 'SoA Import',
                        },
                        element: (
                            <Suspense fallback={null} key="protocol-import">
                                <AppRootAuthorizedRoute appName="protocol-import">
                                    <ProtocolImportModule />
                                </AppRootAuthorizedRoute>
                            </Suspense>
                        ),
                    } as AppRouteObject,
                    {
                        path: 'full-protocol-import/*',
                        breadcrumb: {
                            name: 'Protocol Import Lab',
                        },
                        element: (
                            <Suspense fallback={null} key="full-protocol-import">
                                <AppRootAuthorizedRoute appName="protocol-import">
                                    <ProtocolImportProtocolModule />
                                </AppRootAuthorizedRoute>
                            </Suspense>
                        ),
                    } as AppRouteObject,
                    {
                        path: 'protocol-optimization/*',
                        breadcrumb: {
                            name: 'Protocol Optimization',
                        },
                        element: (
                            <Suspense fallback={null} key="protocol-optimization">
                                <AppRootAuthorizedRoute appName="protocol-optimization">
                                    <ProtocolOptimizationModule />
                                </AppRootAuthorizedRoute>
                            </Suspense>
                        ),
                    } as AppRouteObject,
                    {
                        path: 'notfound',
                        element: <NotFound />,
                    },
                    {
                        path: 'unauthorized',
                        element: <NotFound />,
                    },
                    {
                        path: '*',
                        element: <NotFound />,
                    },
                ],
            },
            {
                path: 'signed-off',
                element: <SignedOffMessage />,
            },
            {
                path: 'timed-out',
                element: <SessionExpiredMessage />,
            },
            {
                path: '*',
                element: <NotFound />,
            },
        ],
    },
];

export default routes;
